import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getShipmentPreparationChecklistFieldsByShipmentId,
  postShipmentPreparationChecklistLog
} from 'services/orders';
import { IShipmentPreparationChecklistField } from 'types/orders';

interface UsePreparationChecklistDrawerParams {
  isOpen: boolean;
  shipmentId?: number;
}

/**
 * Hook personalizado que gestiona el estado y lógica del checklist de preparación de envíos.
 * Maneja la carga de datos, actualización optimista de checkboxes individuales y masivos,
 * y sincronización con el backend.
 * @param params - Parámetros del hook.
 * @param params.isOpen - Indica si el drawer está abierto.
 * @param params.shipmentId - ID del envío para cargar el checklist.
 */
const usePreparationChecklistDrawer = ({ isOpen, shipmentId }: UsePreparationChecklistDrawerParams) => {
  const [preparationChecklistFields, setPreparationChecklistFields] = useState<
    IShipmentPreparationChecklistField[] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [checkedItems, setCheckedItems] = useState<CheckedItemsState>({});
  const [updatingItems, setUpdatingItems] = useState<UpdatingItemsState>({});
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const resetState = useCallback(() => {
    setPreparationChecklistFields(null);
    setHasError(false);
    setCheckedItems({});
    setUpdatingItems({});
    setIsBulkUpdating(false);
    setIsLoading(false);
  }, []);

  const fetchChecklist = useCallback(async () => {
    if (!shipmentId) return;

    setIsLoading(true);
    setHasError(false);

    try {
      const checklistFields = await getShipmentPreparationChecklistFieldsByShipmentId(shipmentId);
      setPreparationChecklistFields(checklistFields);

      const initialCheckedState = checklistFields.reduce<CheckedItemsState>((acc, field) => {
        acc[field.id] = field.shipmentPreparationChecklistLogs?.[0]?.preparationChecklistValue ?? false;
        return acc;
      }, {});
      setCheckedItems(initialCheckedState);
    } catch {
      setHasError(true);
      setPreparationChecklistFields(null);
    } finally {
      setIsLoading(false);
    }
  }, [shipmentId]);

  const checklistItems = useMemo<ChecklistItem[]>(() => {
    if (!preparationChecklistFields?.length) return [];
    return preparationChecklistFields.map(({ id, description }) => ({ id, label: description }));
  }, [preparationChecklistFields]);

  const canBulkUpdate = !!shipmentId && !!preparationChecklistFields?.length;

  const allChecked = useMemo(() => {
    if (!preparationChecklistFields?.length) return false;
    return preparationChecklistFields.every(({ id }) => !!checkedItems[id]);
  }, [preparationChecklistFields, checkedItems]);

  const someChecked = useMemo(() => {
    if (!preparationChecklistFields?.length) return false;
    return preparationChecklistFields.some(({ id }) => !!checkedItems[id]);
  }, [preparationChecklistFields, checkedItems]);

  const handleCheckboxChange = useCallback(
    async (itemId: number) => {
      if (!shipmentId || updatingItems[itemId] || isBulkUpdating) return;

      const previousValue = checkedItems[itemId] || false;
      const nextValue = !previousValue;

      setCheckedItems(prev => ({ ...prev, [itemId]: nextValue }));
      setUpdatingItems(prev => ({ ...prev, [itemId]: true }));

      try {
        await postShipmentPreparationChecklistLog({
          shipmentId,
          preparationChecklistFieldId: itemId,
          preparationChecklistValue: nextValue
        });
      } catch {
        setCheckedItems(prev => ({ ...prev, [itemId]: previousValue }));
      } finally {
        setUpdatingItems(prev => ({ ...prev, [itemId]: false }));
      }
    },
    [shipmentId, checkedItems, updatingItems, isBulkUpdating]
  );

  const handleToggleAll = useCallback(async () => {
    if (!shipmentId || !preparationChecklistFields?.length || isBulkUpdating) return;

    const previousCheckedItems = { ...checkedItems };
    const nextValue = !preparationChecklistFields.every(({ id }) => !!checkedItems[id]);

    const nextCheckedItems = preparationChecklistFields.reduce<CheckedItemsState>(
      (acc, { id }) => ({ ...acc, [id]: nextValue }),
      {}
    );

    setCheckedItems(nextCheckedItems);
    setIsBulkUpdating(true);

    try {
      await Promise.all(
        preparationChecklistFields.map(({ id }) =>
          postShipmentPreparationChecklistLog({
            shipmentId,
            preparationChecklistFieldId: id,
            preparationChecklistValue: nextValue
          })
        )
      );
    } catch {
      setCheckedItems(previousCheckedItems);
    } finally {
      setIsBulkUpdating(false);
    }
  }, [shipmentId, preparationChecklistFields, checkedItems, isBulkUpdating]);

  const hasPendingOperations = isBulkUpdating || Object.values(updatingItems).some(Boolean);

  useEffect(() => {
    if (isOpen && shipmentId) {
      fetchChecklist();
      return;
    }

    if (!isOpen) {
      resetState();
    }
  }, [isOpen, shipmentId, fetchChecklist, resetState]);

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someChecked && !allChecked;
  }, [someChecked, allChecked]);

  return {
    checklistItems,
    checkedItems,
    updatingItems,
    isBulkUpdating,
    isLoading,
    hasError,
    hasPendingOperations,
    canBulkUpdate,
    allChecked,
    selectAllRef,
    handleCheckboxChange,
    handleToggleAll,
    refetch: fetchChecklist
  };
};

export default usePreparationChecklistDrawer;

interface ChecklistItem {
  id: number;
  label: string;
}

type CheckedItemsState = Record<number, boolean>;
type UpdatingItemsState = Record<number, boolean>;
